import { useEffect, useState } from "react";
import { Check } from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import * as actions from "../redux/actions/billing";

const getAddonSeat = (plan) => {
    if (!plan) return null;
    return (plan.seatPrices || []).find((price) => price.seatRole === "addon") || null;
};

const formatPlanPrice = (amount) => {
    const price = Number(amount);
    if (!Number.isFinite(price)) return null;
    return Number.isInteger(price) ? String(price) : price.toFixed(2);
};

const addonBenefits = (plan, periodLabel) => {
    const credits = Number(getAddonSeat(plan)?.creditAmount);
    const lines = ["Full access to all Pro features"];
    if (Number.isFinite(credits)) {
        lines.push(`${credits} AI credits every month`);
    }
    lines.push(periodLabel === "year" ? "Renews annually" : "Renews monthly");
    return lines;
};

/**
 * UpNComer Pro checkout for an added artist.
 * Requires artistProfileId from the add-artist flow.
 */
export const TeamSeatPlanModal = ({
    isOpen,
    onClose,
    returnUrl,
    artistProfileId,
}) => {
    const dispatch = useDispatch();
    const [isLoading, setIsLoading] = useState(false);
    const [planLoading, setPlanLoading] = useState(false);

    const { subscriptionPlan } = useSelector((state) => state.billing);

    const monthlyPlan = (subscriptionPlan || []).find((plan) => plan.interval === "monthly");
    const yearlyPlan = (subscriptionPlan || []).find((plan) => plan.interval === "yearly");
    const planChoices = [
        monthlyPlan ? { plan: monthlyPlan, label: "Monthly", period: "month" } : null,
        yearlyPlan ? { plan: yearlyPlan, label: "Yearly", period: "year" } : null,
    ].filter(Boolean);
    const singlePlan = planChoices.length === 1;

    useEffect(() => {
        if (!isOpen) return;
        dispatch(actions.getPlanList("subscription", setPlanLoading, "addon"));
    }, [dispatch, isOpen]);

    if (!isOpen) return null;

    const handleCheckout = async (planId) => {
        if (!planId || !artistProfileId || isLoading) return;
        await dispatch(
            actions.generateStripeSubscriptionCheckout(
                {
                    planId,
                    returnUrl,
                    seatType: "addon",
                    artistProfileId,
                },
                setIsLoading
            )
        );
    };

    return (
        <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/50 backdrop-blur-sm overflow-auto">
            <div className="bg-gradient-to-br from-[#000456] via-[#0a1a8a] to-[#1e3a8a] rounded-2xl shadow-lg w-full max-w-[860px] mx-2 my-4 animate-fadeIn">
                <div className="modal-body relative px-4 py-8">
                    <button
                        type="button"
                        onClick={onClose}
                        className="absolute top-3 end-4 text-theme_clr2 hover:text-theme_clr4 text-xl transition"
                    >
                        <i className="bi bi-x-lg lh-1" />
                    </button>

                    <div className="text-center mb-8 mt-2">
                        <h2 className="text-2xl font-bold text-white mb-2">Subscribe to UpNComer Pro</h2>
                        <p className="text-gray-300 text-sm max-w-lg mx-auto">
                            {singlePlan
                                ? "Unlock all Pro features for this artist."
                                : "Choose a subscription plan for this artist."}
                        </p>
                    </div>

                    {planLoading && (
                        <p className="text-center text-gray-300 text-sm">Loading plans…</p>
                    )}
                    {!planLoading && planChoices.length > 0 && (
                        <div className={`grid gap-4 ${singlePlan ? "mx-auto max-w-lg" : "sm:grid-cols-2"}`}>
                            {planChoices.map(({ plan, label, period }) => {
                                const addonSeat = getAddonSeat(plan);
                                const price = formatPlanPrice(addonSeat?.price ?? plan.price);
                                return (
                                    <div key={plan.id} className="rounded-xl border border-[#FA6138]/50 bg-white/5 px-6 py-6">
                                        <h3 className="text-center text-xl font-bold text-white">{label}</h3>
                                        <p className="mt-1 text-center text-sm text-gray-400">{plan.name}</p>
                                        {singlePlan && (
                                            <p className="mt-2 text-center text-sm text-gray-300">
                                                Unlock all UpNComer Pro features for this artist.
                                            </p>
                                        )}
                                        <div className="mt-4 text-center text-3xl font-bold text-[#FA6138]">
                                            {price == null ? "—" : `$${price}`}
                                            <span className="text-lg font-normal text-gray-300"> / {period}</span>
                                        </div>
                                        <ul className="mb-6 mt-5 space-y-2">
                                            {addonBenefits(plan, period).map((text) => (
                                                <li key={text} className="flex items-start gap-2 text-sm text-gray-300">
                                                    <Check className="mt-0.5 h-4 w-4 flex-shrink-0 text-[#FA6138]" />
                                                    {text}
                                                </li>
                                            ))}
                                        </ul>
                                        <button
                                            type="button"
                                            disabled={isLoading || !artistProfileId}
                                            onClick={() => handleCheckout(plan.id)}
                                            className={`w-full rounded-lg py-3 font-bold text-white ${
                                                isLoading || !artistProfileId
                                                    ? "cursor-not-allowed bg-gray-500"
                                                    : "bg-[#FA6138] hover:bg-[#e55532]"
                                            }`}
                                        >
                                            {isLoading ? "Processing…" : singlePlan ? "Continue to Payment →" : `Choose ${label}`}
                                        </button>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                    {!planLoading && planChoices.length === 0 && (
                        <p className="text-center text-sm text-gray-300">
                            No UpNComer Pro plans are available right now.
                        </p>
                    )}
                </div>
            </div>
        </div>
    );
};
